import type { ServerConfig, ServerStats } from "@ronin/contracts";
import { formatBytes, formatRate, formatUptime, usageLevel, usagePercent } from "@ronin/core";
import { cn } from "@ronin/ui";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { Clock, Cpu, HardDrive, MemoryStick, Network } from "lucide-react";
import { api } from "../../api.js";
import { useTimeZone } from "../settings/use-settings.js";
import { useHomeFeatures } from "./use-home.js";

/** spec14 FR9: every 10 s while the page is visible. */
export const serverStatsQuery = queryOptions({
  queryKey: ["home", "server-stats"],
  queryFn: () => api.home.serverStats(),
  refetchInterval: 10_000,
  retry: false,
});

const levelText = {
  normal: "text-foreground",
  high: "text-important",
  critical: "text-destructive",
} as const;

interface Meter {
  key: string;
  /** For screen readers; the icon says it on screen. */
  name: string;
  icon: typeof Cpu;
  /** Left: the value; right: what it is ("CPU", "Free"), like a homepage resources widget. */
  value: string;
  label: string;
  /** The bar (used share); none for uptime and network. */
  bar?: { used: number; total: number };
  /** Medium and Large: one more line of detail under the bar. */
  detail: string;
}

/** spec14 FR8: the stats this widget shows (its settings), in a fixed order. */
export function shownStats(config: ServerConfig) {
  return {
    cpu: config.cpu ?? true,
    memory: config.memory ?? true,
    disk: (label: string) => config.disks?.includes(label) ?? true,
    network: config.network ?? false,
    uptime: config.uptime ?? false,
  };
}

function metersOf(stats: ServerStats, config: ServerConfig, timeZone: string): Meter[] {
  const shown = shownStats(config);
  const meters: Meter[] = [];
  if (shown.cpu) {
    meters.push({
      key: "cpu",
      name: "CPU",
      icon: Cpu,
      value: `${Math.round(stats.cpu)} %`,
      label: "CPU",
      bar: { used: stats.cpu, total: 100 },
      detail: `${stats.cores} ${stats.cores === 1 ? "core" : "cores"}`,
    });
  }
  if (shown.memory) {
    const { used, total } = stats.memory;
    meters.push({
      key: "memory",
      name: "Memory",
      icon: MemoryStick,
      value: formatBytes(total - used),
      label: "Free",
      bar: stats.memory,
      detail: `${formatBytes(used)} used of ${formatBytes(total)}`,
    });
  }
  for (const disk of stats.disks.filter((d) => shown.disk(d.label))) {
    meters.push({
      key: `disk-${disk.label}`,
      name: `Storage ${disk.label}`,
      icon: HardDrive,
      value: formatBytes(disk.total - disk.used),
      label: "Free",
      bar: disk,
      detail: `${disk.label} · ${formatBytes(disk.used)} used of ${formatBytes(disk.total)}`,
    });
  }
  if (shown.network && stats.network) {
    meters.push({
      key: "network",
      name: "Network",
      icon: Network,
      value: `↓ ${formatRate(stats.network.rx)}`,
      label: `↑ ${formatRate(stats.network.tx)}`,
      detail: "Download · upload",
    });
  }
  if (shown.uptime) {
    const since = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(Date.now() - stats.uptime * 1000);
    meters.push({
      key: "uptime",
      name: "Uptime",
      icon: Clock,
      value: formatUptime(stats.uptime),
      label: "Up",
      detail: `Since ${since}`,
    });
  }
  return meters;
}

/**
 * spec14 FR8, FR9: the chosen stats, each as icon | value and label | bar. Up to a full row
 * tall they sit in one line; taller, in a grid of about two columns per stat, with details.
 */
export function ServerWidget({ w, h, config }: { w: number; h: number; config: ServerConfig }) {
  const { serverStats } = useHomeFeatures();
  const timeZone = useTimeZone();
  const stats = useQuery({ ...serverStatsQuery, enabled: serverStats });
  const message = !serverStats
    ? "Not set up on this server"
    : stats.isError
      ? "Server stats cannot be read"
      : undefined;

  if (message || !stats.data) {
    return (
      <div className="flex h-full min-w-0 items-center px-3">
        <p className="truncate text-xs text-faint-foreground">{message ?? "Loading …"}</p>
      </div>
    );
  }
  const meters = metersOf(stats.data, config, timeZone);
  const large = h > 4;
  const columns = Math.max(1, Math.min(meters.length, Math.floor(w / 2)));
  return (
    <ul
      // Through the CSSOM (React style), which the CSP allows.
      style={large ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}
      className={cn(
        "h-full min-w-0 overflow-hidden px-3",
        large ? "grid content-center gap-x-5 gap-y-3 py-2" : "flex items-center gap-4",
      )}
    >
      {meters.map((meter) => (
        <MeterItem key={meter.key} meter={meter} detail={large} />
      ))}
    </ul>
  );
}

function MeterItem({ meter, detail }: { meter: Meter; detail: boolean }) {
  const Icon = meter.icon;
  const level = meter.bar ? usageLevel(usagePercent(meter.bar.used, meter.bar.total)) : "normal";
  return (
    <li className="flex min-w-0 flex-1 items-center gap-2.5">
      <Icon className="size-5 shrink-0 text-dim-foreground" aria-hidden />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="sr-only">{meter.name}</span>
        <span className="flex min-w-0 items-baseline justify-between gap-2 text-xs tabular-nums">
          <span className={cn("truncate font-medium", levelText[level])}>{meter.value}</span>
          <span className="shrink-0 text-muted-foreground">{meter.label}</span>
        </span>
        {/* Uptime and network have no bar: their value sits centred next to the icon. */}
        {meter.bar && (
          <progress
            aria-label={meter.name}
            max={meter.bar.total}
            value={meter.bar.used}
            data-level={level}
            className="w-full"
          />
        )}
        {detail && (
          <span className="truncate text-[11px] text-faint-foreground tabular-nums">
            {meter.detail}
          </span>
        )}
      </span>
    </li>
  );
}
